import { Loader2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import logoMark from "@/assets/logo-mark.svg";
import { useAuth } from "@/auth/AuthContext";
import { LedBeamsBackground } from "@/components/LedBeamsBackground";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";

interface LocationState {
  from?: { pathname?: string };
}

export function LoginPage() {
  const { login, isLoggingIn, authenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const redirectTo =
    (location.state as LocationState | null)?.from?.pathname ?? "/";

  if (authenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    try {
      await login({ email, password });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setError("Troppi tentativi. Riprova fra qualche istante.");
        } else if (err.status === 401 || err.status === 400) {
          setError("Credenziali non valide.");
        } else if (
          err.status === 403 &&
          (String(err.message).toLowerCase().includes("banned") ||
            String((err.body as { code?: string } | null)?.code)
              .toLowerCase()
              .includes("ban"))
        ) {
          setError("Account sospeso. Contatta l'amministratore.");
        } else {
          setError("Errore imprevisto. Riprova piu tardi.");
        }
      } else {
        setError("Impossibile contattare il server.");
      }
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#f4f6fb] p-4 text-slate-900">
      {/* Blueprint grid — quiet "automation / system" texture */}
      <div aria-hidden="true" className="login-grid absolute inset-0" />

      {/* Animated AI data-stream beams */}
      <div className="login-fade pointer-events-none absolute inset-0">
        <LedBeamsBackground />
      </div>

      {/* Atmosphere: soft cool highlights + gentle edge fade */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(90% 60% at 50% 30%, rgba(37,99,235,0.10), transparent 60%), radial-gradient(70% 60% at 85% 90%, rgba(14,165,233,0.08), transparent 60%), radial-gradient(120% 120% at 50% 50%, transparent 60%, rgba(226,232,240,0.7) 100%)",
        }}
      />

      <main className="relative z-10 w-full max-w-md">
        {/* Brand mark */}
        <div
          className="login-animate mb-5 flex justify-center"
          style={{ animationDelay: "60ms" }}
        >
          <img
            src={logoMark}
            alt="Nuntius"
            className="h-14 w-14 drop-shadow-[0_14px_34px_-12px_rgba(37,99,235,0.6)]"
          />
        </div>

        {/* Eyebrow */}
        <div
          className="login-animate mb-6 flex items-center justify-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.32em] text-slate-400"
          style={{ animationDelay: "120ms" }}
        >
          <span className="h-px w-6 bg-gradient-to-r from-transparent to-blue-500/50" />
          Corriere del Ticino
          <span className="h-px w-6 bg-gradient-to-l from-transparent to-blue-500/50" />
        </div>

        {/* Frosted card */}
        <div
          className="login-animate relative rounded-2xl border border-slate-200/80 bg-white/80 p-8 shadow-[0_30px_80px_-32px_rgba(15,23,42,0.35)] backdrop-blur-xl sm:p-10"
          style={{ animationDelay: "220ms" }}
        >
          {/* Top hairline accent */}
          <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />

          <header className="mb-8 text-center">
            <h1 className="font-display bg-gradient-to-br from-slate-900 to-blue-700 bg-clip-text text-5xl font-semibold tracking-tight text-transparent">
              Nuntius
            </h1>
            <p className="mt-3 text-sm text-slate-500">
              Accedi con le credenziali della redazione.
            </p>
          </header>

          <form className="space-y-5" onSubmit={handleSubmit} noValidate>
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="text-xs font-medium uppercase tracking-wider text-slate-500"
              >
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="nome@cdt.ch"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoggingIn}
                required
                autoFocus
                className="h-11 border-slate-200 bg-white/70 text-slate-900 placeholder:text-slate-400 focus-visible:border-blue-400 focus-visible:ring-blue-400/25 focus-visible:ring-offset-0"
              />
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="password"
                className="text-xs font-medium uppercase tracking-wider text-slate-500"
              >
                Password
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoggingIn}
                required
                className="h-11 border-slate-200 bg-white/70 text-slate-900 placeholder:text-slate-400 focus-visible:border-blue-400 focus-visible:ring-blue-400/25 focus-visible:ring-offset-0"
              />
            </div>
            {error ? (
              <p
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {error}
              </p>
            ) : null}
            <Button
              type="submit"
              className="group h-11 w-full bg-gradient-to-b from-blue-500 to-blue-700 text-white shadow-[0_10px_30px_-10px_rgba(37,99,235,0.65)] transition-all hover:from-blue-500 hover:to-blue-600 hover:shadow-[0_14px_36px_-10px_rgba(37,99,235,0.8)] disabled:from-slate-300 disabled:to-slate-300 disabled:text-white disabled:shadow-none"
              disabled={
                isLoggingIn || email.length === 0 || password.length === 0
              }
            >
              {isLoggingIn ? (
                <>
                  <Loader2 className="animate-spin" />
                  Accesso in corso...
                </>
              ) : (
                "Accedi"
              )}
            </Button>
          </form>
        </div>

        {/* Footer microcopy */}
        <p
          className="login-animate mt-6 text-center text-xs text-slate-400"
          style={{ animationDelay: "340ms" }}
        >
          Sistema interno · accesso riservato alla redazione
        </p>
      </main>
    </div>
  );
}
